import { useHistory } from 'react-router-dom';
import {
  FaBoxOpen,
  FaExclamationTriangle,
  FaFileInvoice,
  FaIdCard,
  FaLock,
  FaMagic,
  FaShoppingCart,
} from 'react-icons/fa';
import { LoadingContainer, TabsComponent } from '../../components/Layout';
import { MerchantGeneralTab } from './MerchantGeneralTab';
import { MerchantOpeningHoursTab } from './MerchantOpeningHoursTab';
import { MerchantMenuTab } from './MerchantMenuTab';
import { MerchantOrdersSettingsTab } from './MerchantOrdersSettingsTab';
import { StorePayment } from '../StorePayment/StorePayment';
import { MerchantContractDataTab } from './MerchantContractDataTab';
import { StoreDeliveryEditView } from '../StoreDeliveryEdit/StoreDeliveryEditView';

import { MerchantAppAccountsTab } from './MerchantAppAccountsTab';
import { MerchantOrdersTab } from './MerchantOrdersTab';
import { EnableStore, RemoveStore } from './RemoveStore';
import { MerchantGeneralAdminInfoTab } from './MerchantGeneralAdminInfoTab';
import WebsiteWidgetTab from './WebsiteWidgetTab';
import { getActiveTabFromNav } from '@/utils/url';
import i18n from '@/utils/i18n';
import { OfflineCardTab } from './OfflineCardTab';
import { EditMerchantOrder } from '../../components/EditMerchantOrder/EditMerchantOrder';
import { MerchantInvoiceTab } from './MerchantInvoiceTab';
import { MerchantInvoiceSettingsTab } from './MerchantInvoiceSettingsTab';
import { StockTab } from './StockTab';
import { useQuery } from '@apollo/client';
import { adminMinimalStoreQuery } from '../../graphql/queries';
import { MerchantOrderOverviewTab } from './MerchantOrderOverviewTab';
import { VendingMachineTab } from './VendingMachineTab';
import { MerchantIntegrationTab } from './MerchantIntegrationTab';
import { SelfReturnQrCodeTab } from './SelfReturnQrCodeTab';
import { MerchantManualsTab } from './MerchantManualsTab';
import { SendEmailTab } from './SendEmailTab';
import { ChangeHistory } from './ChangeHistory';
import StoreContainerStationsList from './StoreContainerStationsListTab';
import MerchantTransactionsTab from './MerchantTransactionsTab';
import CustomerCateringUserListTab from './CustomerCateringUserListTab';
import {
  OnboardingStatus,
  ServicePackageType,
  StoreType,
} from '../../graphql/generatedTypes';
import InventoryCheckTab from './InventoryCheckTab/InventoryCheckTab';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Alert } from 'reactstrap';
import { MerchantContainersTab } from './AssetsTab/MerchantContainersTab';
import { MerchantOfflineCardsTab } from './AssetsTab/MerchantOfflineCardsTab';
import { MerchantCurrentAssets } from './Assets/MerchantCurrentAssets';
import { MerchantEditAssets } from './Assets/MerchantEditAssets';
import { CommittedTransactionVolumeTab } from './CommittedTransactionVolume/CommittedTransactionVolumeTab';
import { ServicePackageTab } from './ServicePackage/ServicePackageTab';
import { StoreCarrierSettingsTab } from './StoreCarrierSettingsTab';
import { SustainabilityReportsTab } from './SustainabilityReportsTab';

// Query params owned by the partner search on this page (see PartnerSearchNew);
// carried over on tab switches so the search state survives tab navigation.
const PARTNER_SEARCH_PARAMS = ['q', 'deleted', 'events', 'partners', 'stores'];

interface MerchantStorePageContentProps {
  storeId: string;
  brandId: string;
}

export function MerchantStorePageContent(props: MerchantStorePageContentProps) {
  const { storeId, brandId } = props;
  const history = useHistory();

  const { data = { store: null, brand: null } } = useQuery(
    adminMinimalStoreQuery,
    {
      variables: { store_id: storeId, brand_id: brandId },
    },
  );
  const { store, brand }: { store: any; brand: any } = data;

  if (!store || !brand) {
    return <LoadingContainer className="p-3" />;
  }

  const {
    external_vytal_id: vytalStoreGuid,
    permanently_removed: permanentlyRemoved,
    name: storeName,
    removal_reason: removalReason,
  } = store;
  const { external_vytal_id: vytalMerchantGuid } = brand;

  // Mirror the sustainability report eligibility from colugo (getEligibleStores):
  // eligible service package, OR any active store in Spain/US regardless of package.
  const isEligibleForSustainabilityReport =
    [
      ServicePackageType.Essential,
      ServicePackageType.ProPlus,
      ServicePackageType.Enterprise,
    ].includes(store.service_package_type) ||
    (['ES', 'US'].includes(store.country_code) &&
      store.onboarding_status === OnboardingStatus.Active);

  const getParams = () => {
    return {
      params: {
        vytalId: vytalStoreGuid,
        orderId: null,
      },
    };
  };

  const tabs = [
    {
      title: i18n.t('Tabs:profile'),
      icon: <FaIdCard />,
      children: [
        {
          title: i18n.t('Tabs:generalInfo'),
          content: (
            <div data-use-bs={true}>
              <MerchantGeneralTab
                externalPickpackId={storeId}
                title={i18n.t('Tabs:generalInfo')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:openingHours'),
          content: (
            <div data-use-bs={true}>
              <MerchantOpeningHoursTab
                externalPickpackId={storeId}
                title={i18n.t('Common:openingHours')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:menu'),
          content: (
            <div data-use-bs={true}>
              <MerchantMenuTab
                externalPickpackId={storeId}
                title={i18n.t('Tabs:menu')}
              />
            </div>
          ),
        },
      ],
    },
    {
      title: i18n.t('Tabs:configuration'),
      icon: <FaMagic />,
      children: [
        {
          // Stays first in this group so existing ?activeTab=1_0 links keep
          // resolving here. No `data-use-bs` wrapper: the tab is shadcn, and
          // Bootstrap scope resets `display` on its img/svg/iframe.
          title: i18n.t('Tabs:help'),
          content: <MerchantManualsTab title={i18n.t('Tabs:help')} />,
        },
        {
          title: i18n.t('SelfReturnQrCodesTab:selfReturnQrCodes'),
          content: (
            <div data-use-bs={true}>
              <SelfReturnQrCodeTab
                storeUuid={vytalStoreGuid}
                storeTextId={storeId}
                title={i18n.t('SelfReturnQrCodesTab:selfReturnQrCodes')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:integrations'),
          content: (
            <div data-use-bs={true}>
              <MerchantIntegrationTab
                vytalStoreId={vytalStoreGuid}
                title={i18n.t('Tabs:integrations')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:banner'),
          content: (
            <div data-use-bs={true}>
              <WebsiteWidgetTab
                merchantStoreId={vytalStoreGuid}
                title={i18n.t('Common:banner')}
              />
            </div>
          ),
        },
      ],
    },
    {
      title: i18n.t('Tabs:payments'),
      icon: <FaFileInvoice />,
      children: [
        {
          title: i18n.t('Common:paymentMehtods'),
          content: (
            <div data-use-bs={true}>
              <StorePayment
                storeId={storeId}
                title={i18n.t('Common:paymentMehtods')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:invoices'),
          content: (
            <div data-use-bs={true}>
              <MerchantInvoiceTab
                externalPickpackId={storeId}
                title={i18n.t('Common:invoices')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Merchants:invoiceSettings'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantInvoiceSettingsTab
                externalPickpackId={storeId}
                title={i18n.t('Merchants:invoiceSettings')}
              />
            </div>
          ),
        },
        {
          title: 'Service Package',
          icon: <FaLock />,
          content: (
            <ServicePackageTab
              storeId={vytalStoreGuid}
              storePickpackId={store.id}
              currency={store.currency || 'EUR'}
              invoiceYearly={store.invoice_yearly ?? null}
              onboardedDate={store.onboarded_date ?? null}
              servicePackageStartDate={store.service_package_start_date ?? null}
              freeCheckoutSegment={store.free_checkout_segment ?? null}
              sustainabilityReportsEnabled={
                store.sustainability_reports_enabled !== false
              }
            />
          ),
        },
        {
          title: 'Committed Volume',
          icon: <FaLock />,
          content: (
            <CommittedTransactionVolumeTab
              storeId={vytalStoreGuid}
              currency={store.currency || 'EUR'}
            />
          ),
        },
      ],
    },
    {
      title: i18n.t('Tabs:containersAndStock'),
      icon: <FaBoxOpen />,
      children: [
        {
          title: i18n.t('Common:stock'),
          content: (
            <div data-use-bs={true}>
              <StockTab
                vytalStoreId={vytalStoreGuid}
                pickpackStoreId={storeId}
                title={i18n.t('Common:stock')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:transactions'),
          content: (
            <div data-use-bs={true}>
              <MerchantTransactionsTab
                vytalStoreGuid={vytalStoreGuid}
                title={i18n.t('Common:transactions')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:containerOrders'),
          content: (
            <div data-use-bs={true}>
              <MerchantOrderOverviewTab
                vytalStoreId={vytalStoreGuid}
                title={i18n.t('Common:containerOrders')}
              />
            </div>
          ),
        },
        {
          title: 'Assets',
          content: <MerchantCurrentAssets storeId={storeId} />,
        },
        {
          title: i18n.t('Common:offlineCards'),
          content: (
            <div data-use-bs={true}>
              <OfflineCardTab
                vytalStoreGuid={vytalStoreGuid}
                title={i18n.t('Common:offlineCards')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('ContainerStation:containerStation'),
          content: (
            <div data-use-bs={true}>
              <StoreContainerStationsList
                storeTextId={storeId}
                storeUuid={vytalStoreGuid}
                title={i18n.t('ContainerStation:containerStation')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:inventoryCheck'),
          content: (
            <div data-use-bs={true}>
              <InventoryCheckTab
                vytalStoreGuid={vytalStoreGuid}
                title={i18n.t('Common:inventoryCheck')}
              />
            </div>
          ),
        },
        ...(isEligibleForSustainabilityReport
          ? [
              {
                title: i18n.t('SustainabilityReports:tabTitle'),
                content: (
                  <div data-use-bs={true}>
                    <SustainabilityReportsTab
                      vytalStoreId={vytalStoreGuid}
                      title={i18n.t('SustainabilityReports:tabTitle')}
                      isEligible={isEligibleForSustainabilityReport}
                      storePickpackId={store.id}
                      sendReports={
                        store.sustainability_reports_enabled !== false
                      }
                    />
                  </div>
                ),
              },
            ]
          : []),
        ...(store?.type === StoreType.Caterer
          ? [
              {
                title: i18n.t('Common:customerCateringUsers'),
                content: (
                  <div data-use-bs={true}>
                    <CustomerCateringUserListTab
                      storeTextId={storeId}
                      title={i18n.t('Common:customerCateringUsers')}
                    />
                  </div>
                ),
              },
            ]
          : []),
      ],
    },
    {
      title: i18n.t('Tabs:orders'),
      icon: <FaShoppingCart />,
      children: [
        {
          title: i18n.t('Common:onlineOrders'),
          content: (
            <div data-use-bs={true}>
              <MerchantOrdersTab
                externalPickpackId={storeId}
                title={i18n.t('Common:onlineOrders')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:ordersSetting'),
          content: (
            <div data-use-bs={true}>
              <MerchantOrdersSettingsTab
                externalPickpackId={storeId}
                vytalStoreId={vytalStoreGuid}
                title={i18n.t('Tabs:ordersSetting')}
              />
            </div>
          ),
        },
      ],
    },
    {
      title: i18n.t('Tabs:admin'),
      icon: <FaLock />,
      children: [
        {
          title: i18n.t('Merchants:generalAdminInfo'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantGeneralAdminInfoTab
                externalPickpackId={storeId}
                title={i18n.t('Merchants:generalAdminInfo')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Merchants:contractData'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantContractDataTab
                externalPickpackId={storeId}
                title={i18n.t('Merchants:contractData')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Merchants:delivery'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <StoreDeliveryEditView
                externalPickpackId={storeId}
                title={i18n.t('Merchants:delivery')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Carrier:carrierSettings'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <StoreCarrierSettingsTab
                storeId={storeId}
                vytalStoreGuid={vytalStoreGuid}
                title={i18n.t('Carrier:carrierSettings')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Assets:editStoreAssets'),
          icon: <FaLock />,
          content: <MerchantEditAssets storeId={storeId} />,
        },
        {
          title: i18n.t('Merchants:editContainers'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantContainersTab
                vytalStoreGuid={vytalStoreGuid}
                externalPickpackId={storeId}
                title={i18n.t('Merchants:editContainers')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Merchants:editOfflineCards'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantOfflineCardsTab
                vytalStoreGuid={vytalStoreGuid}
                externalPickpackId={storeId}
                title={i18n.t('Merchants:editOfflineCards')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:partnerAppAccount'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <MerchantAppAccountsTab
                vytalStoreGuid={vytalStoreGuid}
                brand={brand}
                storeType={store?.type}
                title={i18n.t('Tabs:partnerAppAccount')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('MerchantOrder:create'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <EditMerchantOrder
                match={getParams()}
                title={i18n.t('MerchantOrder:create')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('containerSearch:searchContainers') + ' 🔗',
          icon: <FaLock />,
          content: <div />,
          onClick: () =>
            history.push(
              `/containers/?searchStoreId=true&merchantId=${vytalMerchantGuid}&storeId=${vytalStoreGuid}&isCurrentLocation=true`,
            ),
        },
        {
          title: i18n.t('Common:vendingMachines'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <VendingMachineTab
                pickpackStoreId={storeId}
                title={i18n.t('Common:vendingMachines')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:sendEmailTab'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <SendEmailTab
                storeTextId={storeId}
                title={i18n.t('Tabs:sendEmailTab')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Tabs:changeHistory'),
          icon: <FaLock />,
          content: (
            <div data-use-bs={true}>
              <ChangeHistory
                storeTextId={storeId}
                title={i18n.t('Tabs:changeHistory')}
              />
            </div>
          ),
        },
        {
          title: i18n.t('Common:delete'),
          icon: <FaLock />,
          content: (
            <ContentSection
              title={i18n.t('Common:delete')}
              collapsable={false}
              key="content"
              data-use-bs={true}
            >
              {permanentlyRemoved && (
                <Alert color="warning" className="mb-3">
                  <div className="d-flex align-items-center">
                    <FaExclamationTriangle className="me-2" />
                    <span>{i18n.t('Merchants:storeRemovedUsersWarning')}</span>
                  </div>
                </Alert>
              )}

              {permanentlyRemoved ? (
                <EnableStore
                  storeName={storeName}
                  externalStoreId={storeId}
                  removalReason={removalReason}
                />
              ) : (
                <RemoveStore
                  storeName={storeName}
                  externalStoreId={storeId}
                  contractEndDate={store.contract_end_date}
                  removalReason={removalReason}
                />
              )}

              <pre className="mt-3">pickpack storeId: {storeId}</pre>
              <pre>vytal storeId: {vytalStoreGuid}</pre>
            </ContentSection>
          ),
        },
      ],
    },
  ];

  const activeTab = getActiveTabFromNav();

  return (
    <TabsComponent
      className="tw:mt-3 tw:mb-4"
      tabs={tabs}
      activeTab={activeTab}
      preserveParams={PARTNER_SEARCH_PARAMS}
    />
  );
}
